import {
    METHOD_INCLUDE_ADGROUP_ON_CONDITION, METHOD_INCLUDE_CAMPAIGN_ON_CONDITION, METHOD_SELECT_ADGROUPS_FROM_LIST,
    METHOD_SELECT_CAMPAIGNS_FROM_LIST,
} from '../business_units/accounts/selectionMethods';

// La lógica pura de «lo que la línea deja afuera» (tanda «Control de líneas», caso Kendal, 15/10): el chip
// del Tablero y del panel del cliente, y su popover. Sin React ni red: se testea sola
// (`__tests__/uncoveredSpend.test.js`).
//
// El dato lo calcula UNA vez el sync del Tablero (`UncoveredSpendManager` del backend) y llega como
// `uncovered_spend` en la fila de la línea y de cada cuenta (contrato §2.1), o por el endpoint de detalle
// `business-unit/<id>/uncovered-spend/` (§2.3). Acá no se recalcula nada: se lee, se agrupa por cuenta y se
// descuenta lo que el usuario ya resolvió en pantalla, hasta que el próximo sync traiga el número nuevo.
//
// Desde la tanda «Inversión sin línea» (16/10) la fila de la línea muestra sólo la FUGA DE SU REGLA
// (`rule_leak`): lo que su «no contar las que contienen…» deja afuera. Lo que no cuenta ninguna línea en una
// cuenta vive en un solo lugar desde UD-5 (17/10): la solapa «Inversión sin control», a la que llevan la
// tarjeta gris del Tablero y de la ficha (`alerts_center/uncontrolled/UncontrolledSummaryCard`).

const RAW = {interpolation: {escapeValue: false}};
const TERM_SEPARATOR = '|||';

// Cuántas campañas se nombran en el popover: el resto lo dice «+ N más» y está entero en «Inversión sin
// control». El backend manda hasta 10, de mayor a menor.
export const CAMPAIGNS_SHOWN = 5;

/**
 * ¿Hay algo que mostrar? Sin el campo (backend viejo) o `null`, no hay chip. Un monto en 0 tampoco, SALVO
 * que el backend avise `has_unconverted` con campañas: es plata en otra moneda que no se pudo convertir a la
 * de la línea (contrato §2.3, C-10), no «nada».
 */
export function hasUncoveredSpend(uncovered) {
    if (!uncovered) return false;
    if (Number(uncovered.amount) > 0) return true;
    return hasUnconverted(uncovered) && Number(uncovered.campaign_count) > 0;
}

/**
 * Lo que la fila de la línea (o de una cuenta) tiene que mostrar: la fuga de SU regla (contrato §2.1).
 * - Sin la clave `rule_leak` (backend viejo, o el backend no pudo armarla): el dato entero, como hasta hoy.
 * - `rule_leak: null`: ninguna regla de la línea avisa, o ya no queda nada. No hay chip.
 * - Con datos: la fuga encima del dato de la línea, que conserva `currency`, `date`, `computed_on`,
 *   `total_budget` e `is_snoozed`, lo que usan el chip y el popover. El resultado ya no trae `rule_leak`:
 *   aplicarlo dos veces, o después de `withResolutions`, no vuelve a pisar lo descontado (review C-cs-fe-14).
 */
export function ruleLeakOf(uncovered) {
    if (!uncovered) return null;
    if (!Object.prototype.hasOwnProperty.call(uncovered, 'rule_leak')) return uncovered;
    const {rule_leak: leak, ...rest} = uncovered;
    return leak ? {...rest, ...leak} : null;
}

/** Campañas afuera cuyo monto no se pudo convertir: van en la lista con su moneda y no suman a `amount`. */
export function hasUnconverted(uncovered) {
    return !!uncovered && uncovered.has_unconverted === true;
}

/** ¿Hay un monto convertido que decir? Con `amount` en 0 (todo sin convertir), el chip no dice «CLP 0». */
export function hasConvertedAmount(uncovered) {
    return !!uncovered && Number(uncovered.amount) > 0;
}

// «Inversión que la línea deja afuera» tiene UN solo umbral (decisión de fede, 17/10): desde el 20 % el
// backend manda `CRITIC` (rojo, y avisa); por debajo, `NORMAL` (gris neutro, `-normal`). No hay «Malo»: un
// `BAD` que mande un backend viejo también cae en el gris.
const LEVEL_MODIFIERS = {CRITIC: '-critic'};

export function levelModifier(level) {
    return LEVEL_MODIFIERS[level] || '-normal';
}

export function accountKey(channel, accountId) {
    return `${channel}|${String(accountId)}`;
}

export function campaignKey(campaign) {
    return `${accountKey(campaign.channel, campaign.account_id)}|${String(campaign.campaign_id)}`;
}

/**
 * Los textos de una condición, legibles: el backend guarda varios términos separados por `|||`
 * (`campaignsPreview.matchesCondition`) y mostrar el separador crudo no le dice nada a nadie.
 */
export function conditionText(text) {
    if (text === null || text === undefined) return null;
    const terms = String(text).split(TERM_SEPARATOR).map((term) => term.trim()).filter(Boolean);
    return terms.length ? terms.join(' / ') : null;
}

/** La regla de una cuenta, en palabras (copy `line_uncovered.popover.rule_*`). `null` si no hay qué decir. */
export function ruleLabel(rule, t) {
    const method = rule && rule.method;
    if (method === METHOD_SELECT_CAMPAIGNS_FROM_LIST || method === METHOD_SELECT_ADGROUPS_FROM_LIST) {
        return t('line_uncovered.popover.rule_list');
    }
    if (method !== METHOD_INCLUDE_CAMPAIGN_ON_CONDITION && method !== METHOD_INCLUDE_ADGROUP_ON_CONDITION) return null;

    const include = conditionText(rule.include_containing);
    const exclude = conditionText(rule.exclude_containing);
    if (include && exclude) return t('line_uncovered.popover.rule_both', {include, exclude, ...RAW});
    if (exclude) return t('line_uncovered.popover.rule_exclude', {text: exclude, ...RAW});
    if (include) return t('line_uncovered.popover.rule_include', {text: include, ...RAW});
    return null;
}

/**
 * La cuenta de la fila que corresponde a una regla: `accounts` son las cuentas que ya tiene la pantalla
 * (`campaigns_configuration_rows` del Tablero: `{id, channel, account_id, name}`; las del panel no traen
 * canal). De ahí sale el nombre, cuando la regla no lo trae.
 */
function accountFor(rule, accounts) {
    const list = accounts || [];
    return list.find((account) => account.channel && accountKey(account.channel, account.account_id)
            === accountKey(rule.channel, rule.account_id))
        || list.find((account) => !account.channel && String(account.account_id) === String(rule.account_id))
        || null;
}

/**
 * Las reglas del dato, siempre como lista. La fila de la línea manda `rules` (una por cuenta); la de una
 * cuenta puede mandar la suya sola (`rule`) y sin decir de qué cuenta es, porque es la de la fila: en ese
 * caso la cuenta sale de `accounts`, que ahí es una sola.
 */
export function rulesOf(uncovered, accounts) {
    if (!uncovered) return [];
    const rules = Array.isArray(uncovered.rules) ? uncovered.rules : (uncovered.rule ? [uncovered.rule] : []);
    const sole = accounts && accounts.length === 1 ? accounts[0] : null;
    return rules.map((rule) => (rule.account_id || !sole
        ? rule
        : {...rule, account_id: sole.account_id, channel: rule.channel || sole.channel}));
}

/**
 * Una entrada por cuenta de la línea (una por regla), con su regla en palabras y sus campañas.
 *
 * El popover de la línea ya no ofrece «Sumarlas» (tanda «Inversión sin línea»): la fila muestra sólo la
 * fuga de una regla POR NOMBRE, y sumar a mano lo que una condición deja afuera no sirve —la condición las
 * volvería a dejar afuera—. La salida es «Cambiar cómo se eligen». Sumar campañas a una lista vive en
 * «Inversión sin control» («¿A qué línea?», `alerts_center/uncontrolled/UncontrolledItem`).
 */
export function accountGroups(uncovered, accounts, t) {
    const campaigns = (uncovered && uncovered.campaigns) || [];
    return rulesOf(uncovered, accounts).map((rule) => {
        const key = accountKey(rule.channel, rule.account_id);
        const account = accountFor(rule, accounts);
        return {
            key,
            rule,
            channel: rule.channel,
            accountId: rule.account_id,
            name: rule.account_name || (account && account.name) || String(rule.account_id),
            ruleText: ruleLabel(rule, t),
            campaigns: campaigns.filter((campaign) => accountKey(campaign.channel, campaign.account_id) === key),
        };
    });
}

/**
 * Las campañas que se nombran (las `CAMPAIGNS_SHOWN` de más inversión de la línea, convertida a su
 * moneda) y cuántas quedan sin nombrar. El conteo sale de `campaign_count`, no de la lista: el backend
 * manda hasta 10 aunque haya más.
 */
export function shownCampaigns(uncovered) {
    const campaigns = [...((uncovered && uncovered.campaigns) || [])]
        .sort((a, b) => (Number(b.amount_converted) || 0) - (Number(a.amount_converted) || 0));
    const shown = campaigns.slice(0, CAMPAIGNS_SHOWN);
    const total = Math.max(Number(uncovered && uncovered.campaign_count) || 0, campaigns.length);
    return {shown, more: Math.max(0, total - shown.length)};
}

// Lo que puede estar corrido el reloj del navegador respecto del servidor (`markedAt` es del navegador y
// `computed_on`, del servidor). Equivocarse para este lado sólo sigue descontando un rato algo que ya está
// resuelto; para el otro, lo resuelto volvería a aparecer.
export const CLOCK_MARGIN_MS = 10 * 60 * 1000;

/**
 * ¿La marca descuenta esta campaña de este dato? Sí, si el dato se calculó antes de marcarla (con el margen
 * del reloj) o si no dice cuándo se calculó. `true` (sin hora) descuenta siempre.
 */
export function stillResolved(markedAt, data) {
    if (!markedAt) return false;
    if (markedAt === true) return true;
    const computedOn = Date.parse(data && data.computed_on);
    if (!Number.isFinite(computedOn)) return true;
    return computedOn < markedAt + CLOCK_MARGIN_MS;
}

/**
 * El dato con lo que el usuario ya resolvió en pantalla descontado, para que nada siga diciendo lo mismo
 * hasta que el backend lo refleje (mismo criterio que «Inversión sin control», que descuenta lo resuelto sin
 * volver a pedir el informe). Sirve igual para la fuga de una línea, el dato de una cuenta y un grupo «sin
 * línea»: los tres traen `amount`, `campaign_count` y `campaigns`.
 *
 * `resolved`: `{[campaignKey]: markedAt}` (`uncoveredResolutions.js`), POR CAMPAÑA: lo descartado y lo que
 * el backend confirmó que sumó o que ya estaba, siempre campañas de la lista. Cada una sale con su monto y
 * resta una del conteo, si el dato se calculó antes de marcarla (`stillResolved`): uno calculado después ya
 * es la verdad del backend. Es idempotente: una campaña resuelta que ya no viene en el dato (el backend ya la
 * descontó) no resta nada, así que un dato nuevo no se descuenta dos veces. Si el dato trae reglas, una
 * cuenta se va del popover cuando no le queda ninguna campaña Y la lista está completa (si viene cortada,
 * puede tener otras que no se nombran).
 *
 * Devuelve `null` cuando ya no queda nada: el chip, o el grupo, desaparece. `accounts`, como en
 * `accountGroups`.
 */
export function withResolutions(data, resolved, accounts) {
    if (!hasUncoveredSpend(data)) return null;
    if (!resolved || !Object.keys(resolved).length) return data;

    let removedAmount = 0;
    let removedCount = 0;
    const campaigns = (Array.isArray(data.campaigns) ? data.campaigns : []).filter((campaign) => {
        if (!stillResolved(resolved[campaignKey(campaign)], data)) return true;
        removedAmount += Number(campaign.amount_converted) || 0;
        removedCount += 1;
        return false;
    });
    if (removedCount === 0) return data;

    // Una campaña sin convertir no tiene `amount_converted`: resta del conteo y no del monto.
    const amount = Math.max(0, Number(data.amount) - removedAmount);
    const count = Math.max(0, (Number(data.campaign_count) || 0) - removedCount);
    if (count === 0 || (amount <= 0 && !hasUnconverted(data))) return null;

    const next = {...data, amount, campaign_count: count, campaigns};
    const totalBudget = Number(data.total_budget);
    if (totalBudget > 0) next.completion = (amount / totalBudget) * 100;
    if (Array.isArray(data.rules) || data.rule) {
        const listComplete = campaigns.length >= count;
        const accountsLeft = new Set(campaigns.map((campaign) => accountKey(campaign.channel, campaign.account_id)));
        next.rules = rulesOf(data, accounts)
            .filter((rule) => !listComplete || accountsLeft.has(accountKey(rule.channel, rule.account_id)));
        next.rule = undefined;
    }
    return next;
}

/** «69» para «69 % del presupuesto». Sin presupuesto (o sin el dato), `null`: el texto cambia. */
export function completionPct(uncovered) {
    const completion = Number(uncovered && uncovered.completion);
    const totalBudget = Number(uncovered && uncovered.total_budget);
    if (!(totalBudget > 0) || !Number.isFinite(completion)) return null;
    return Math.round(completion);
}
